import React, { useEffect, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { Provider } from "react-redux";
import { Toaster } from "react-hot-toast";
import "./App.css";
import "./i18n";
import AuthProtection from "./Components/AuthProtection/AuthProtection";
import { AuthProvider } from "./Context/AuthContext";
import { ThemeProvider } from "./Context/ThemeContext";
import { CartProvider } from "./Context/CartContext";
import InAppBasketAddress from "./Components/InAppBasketAddress/InAppBasketAddress";
import { CurrencyProvider, ReduxCurrencySync } from "./Context/CurrencyContext";
import StickyCartBar, { CartAbandonmentTracker } from "./Components/CartBar";
import SplashGate from "./Components/SplashGate/SplashGate";
import MetaPixelPageViews from "./Components/MetaPixelPageViews";
import ShopJourneyPages from "./Components/ShopJourneyPages";
import RtlSync from "./Components/RtlSync";
import ShopLanguageSync from "./Components/ShopLanguageSync";
import ShopLanguageGate from "./Components/ShopLanguageGate";
import ShopFloatingDock from "./Components/ShopFloatingDock";
import ShopAgentChat from "./Components/AgentChat/ShopAgentChat";
import PageChunkFallback from "./Components/PageChunkFallback";
import { lazyPage } from "./utils/lazyPage";
import { prefetchShopperPages } from "./utils/prefetchShopperPages";
import ProductDetail from "./Pages/ProductDetail";
import store from "./Store/index";
import { bindCustomerScrollPassthrough } from "./utils/forwardVerticalWheel";
import { bindScrollingFlag } from "./utils/scrollingFlag";
import { isDomainMode, getDomainHost } from "./utils/domainMode";
import { initialUiLanguage } from "./utils/shopLanguage";
import { initGtm, gtmEvent } from "./utils/gtm";
/* THE AD SHOPPER'S FIRST PAGE IS THE ONLY ONE IN THE MAIN SCRIPT (2026-10-01). The shop home, the checkout and the
   order-placed page are a tap away, not on screen: their chunks are fetched once the page has loaded and the line
   is quiet (utils/prefetchShopperPages — a <link rel=prefetch> in the head shared the 4G line with the main script
   and cost the buy button a second), so the hop costs nothing, while the landing pays for the product page alone —
   the shop's 184 KB and the checkout's 102 KB (and their stylesheets) came down before the buy button could paint. */
const Shop = lazyPage(() => import("./Pages/Shop"));
const Checkout = lazyPage(() => import("./Pages/Checkout"));
const OrderPlaced = lazyPage(() => import("./Pages/Checkout/OrderPlaced"));
/* THE SHOPPER'S PATH FROM AN AD IS IN THE MAIN SCRIPT; EVERY OTHER PAGE IS ITS OWN CHUNK (2026-10).
   An ad lands on a product; the shop, the checkout and the order-placed page are the
   taps after it, so those four load with the app. Booking, courses, stays, the
   customer's account, the platform's discovery pages and the login page are
   `lazyPage`s (utils/lazyPage.js): fetched the first time a route needs them,
   behind the one <Suspense> below. Before this every page shipped in one 3.5 MB
   script, and a phone on cellular took eight seconds to show a buy button. */
const CustomerLayout = lazyPage(() => import("./Components/Navbar/CustomerLayout"));
const AddAnAppointment = lazyPage(() => import("./Pages/AddApointment"));
const BookedSuccessfull = lazyPage(() => import("./Pages/BookedSuccessfull/BookedSuccessfull"));
const AppointmentDetails = lazyPage(() => import("./Pages/Home/AppointmentDetails/AppointmentDetails"));
const CustomerHomer = lazyPage(() => import("./Pages/Home/CustomerHome"));
const SeeAllAppointments = lazyPage(() => import("./Pages/Home/SeeAllAppointments"));
const Home = lazyPage(() => import("./Pages/Home"));
const ContactUsPage = lazyPage(() => import("./Pages/ContactUs/ContactUsPage"));
const Gallery = lazyPage(() => import("./Pages/Gallery"));
const LegalPage = lazyPage(() => import("./Pages/Legal/Legal"));
const PackageDetails = lazyPage(() => import("./Pages/PackageDetails"));
const CollectionPage = lazyPage(() => import("./Pages/Shop/CollectionPage"));
const BrandPage = lazyPage(() => import("./Pages/Shop/BrandPage"));
const AllCollectionsPage = lazyPage(() => import("./Pages/Shop/AllCollectionsPage"));
const PropertyStay = lazyPage(() => import("./Pages/PropertyStay"));
const PropertiesStays = lazyPage(() => import("./Pages/PropertiesStays"));
const CoursePage = lazyPage(() => import("./Pages/CoursePage"));
const CourseCatalogue = lazyPage(() => import("./Pages/CourseCatalogue"));
const PrivacyPolicy = lazyPage(() => import("./Pages/PrivacyPolicy/PrivacyPolicy"));
const Profile = lazyPage(() => import("./Pages/Profile/Profile"));
const MyOrders = lazyPage(() => import("./Pages/MyOrders"));
const MyPlans = lazyPage(() => import("./Pages/MyPlans"));
const MyCourses = lazyPage(() => import("./Pages/MyCourses"));
const OrderDetails = lazyPage(() => import("./Pages/OrderDetails"));
const StayDetails = lazyPage(() => import("./Pages/StayDetails"));
const MyWishlist = lazyPage(() => import("./Pages/MyWishlist"));
const LoginOrRegiser = lazyPage(() => import("./Pages/RegiserationAndLogin/LoginOrRegiser"));
const PaymentsIframeCallback = lazyPage(() => import("./Pages/PaymentsIframeCallback"));
const PayRequest = lazyPage(() => import("./Pages/PayRequest"));
const PayPosIntent = lazyPage(() => import("./Pages/PayPosIntent"));
const Receipt = lazyPage(() => import("./Pages/Receipt"));
const CustomerOnboard = lazyPage(() => import("./Pages/CustomerOnboard/CustomerOnboard"));
const TermsOfService = lazyPage(() => import("./Pages/TermsOfService/TermsOfService"));
const ViewPackage = lazyPage(() => import("./Pages/ViewPackage"));
const CategoryPage = lazyPage(() => import("./Pages/CategoryPage/CategoryPage"));
const CityPage = lazyPage(() => import("./Pages/CityPage/CityPage"));
const AllBusinessesPage = lazyPage(() => import("./Pages/AllBusinessesPage/AllBusinessesPage"));


function App() {
  const { i18n } = useTranslation();

  useEffect(() => bindCustomerScrollPassthrough(document.body), []);
  useEffect(() => bindScrollingFlag(window), []);

  // Platform GTM/GA4 (separate from the per-salon Meta Pixel, which is
  // initialized from the salon payload in getSalon.js). No-op unless
  // REACT_APP_GTM_ID is set — see utils/gtm.js.
  useEffect(() => { prefetchShopperPages(); }, []);
  useEffect(() => {
    initGtm(process.env.REACT_APP_GTM_ID);
    gtmEvent("page_view", { page_path: window.location.pathname });
  }, []);

  // Use manual selection (localStorage) if set; otherwise browser language as default.
  // On a shop's own page whose languages this browser has seen, the shop's
  // language instead -- the same answer index.js booted with (utils/shopLanguage).
  useEffect(() => {
    const initial = initialUiLanguage({
      pathname: window.location.pathname,
      domainMode: isDomainMode(),
      hostname: getDomainHost(),
    });
    if (i18n.language !== initial) {
      i18n.changeLanguage(initial);
    }
    const dir = ["ar", "he"].includes(initial) ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", initial);
  }, [i18n]);
  
  const dir = ["ar", "he"].includes(i18n.language) ? "rtl" : "ltr";

  return (
    <div dir={dir} key={i18n.language} style={{ minHeight: "100vh", width: "100%" }}>
      <AuthProvider>
        <Provider store={store}>
          <Router>
            <ThemeProvider>
              <CartProvider>
              <CurrencyProvider>
              <ReduxCurrencySync />
              {/* Removes the branded boot splash (#splash, written during HTML
                  parse) once the app is ready. Mounted here, outside <Routes>,
                  so every route is covered without any per-page wiring. */}
              <SplashGate />
              {/* Each page change is a PageView for its own shop's pixel only. */}
              <MetaPixelPageViews />
              {/* …and a step of the journey of a shopper who came from an ad. */}
              <ShopJourneyPages />
              {/* Inside an app's browser the address carries the basket; in a real one it restores it. */}
              <InAppBasketAddress />
              <RtlSync />
              {/* On a shop's own pages, the language the shop is written in:
                  the gate settles it before a shop page is built (so the page is
                  built once), the sync remembers what the pages learn. */}
              <ShopLanguageSync />
              <Toaster position="top-center" />
              <ShopLanguageGate>
              <Suspense fallback={<PageChunkFallback />}>
              <Routes>
            {isDomainMode() ? (
              <>
                {/* Custom-domain (single-tenant) mode: the whole site IS the
                    salon resolved by host, so every salon-scoped route below
                    mirrors the platform-mode route list minus the leading
                    slug segment. Non-salon-scoped routes (customer account:
                    /home, /profile*, /orders/*, /stays/:id, /appointments,
                    /booking, /legal, /terms, /privacy, /contact-us,
                    /see-all-appointments, /appointment-details/:_id,
                    /booking-successful, /payments/iframe-callback,
                    /pay/pos/:intentId) are already slug-less today, so they
                    render unchanged in this branch too. SEO discovery routes
                    (/category, /city, /stores, /explore) are platform-only
                    discovery concepts and are intentionally omitted here. Auth
                    routes (/login, /register, /customer-onboard) ARE included
                    below — customers sign in on the branded domain (same
                    platform user) to reach their bookings/profile. */}
                <Route path="/payments/iframe-callback" element={<PaymentsIframeCallback />} />
                <Route path="/pay/pos/:intentId" element={<PayPosIntent />} />
                <Route path="/receipt/:key" element={<Receipt />} />
                {/* A payment request, keyed by its own opaque token. Declared
                    AFTER the POS route so the more specific two-segment path
                    keeps winning. */}
                <Route path="/pay/:token" element={<PayRequest />} />

                <Route path="/home" element={<AuthProtection><CustomerHomer /></AuthProtection>} />
                <Route path="/profile" element={<AuthProtection><CustomerLayout><Profile /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/orders"   element={<AuthProtection><CustomerLayout><MyOrders /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/plans"     element={<AuthProtection><CustomerLayout><MyPlans /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/plans/:planId" element={<AuthProtection><CustomerLayout><MyPlans /></CustomerLayout></AuthProtection>} />
                {/* Courses, the student's own — design §13.6, wave 3 (self-paced) */}
                <Route path="/profile/courses" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/courses/:enrollmentId" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/courses/:enrollmentId/lessons/:lessonId" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                {/* One meeting of a course, opened from the schedule: what it covered,
                    its homework, its place, its files, and the lessons linked to it. */}
                <Route path="/profile/courses/:enrollmentId/meetings/:sessionId" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                <Route path="/orders/:id"       element={<AuthProtection><OrderDetails /></AuthProtection>} />
                <Route path="/stays/:id"        element={<AuthProtection><StayDetails /></AuthProtection>} />
                <Route path="/profile/wishlist" element={<AuthProtection><CustomerLayout><MyWishlist /></CustomerLayout></AuthProtection>} />
                <Route path="/appointments" element={<AuthProtection><CustomerHomer /></AuthProtection>} />
                <Route path="/booking" element={<AuthProtection><CustomerHomer /></AuthProtection>} />

                {/* Salon-scoped routes, slug-less (mirrors the platform-mode
                    /:username, /:salonSlug, /:slug variants below). */}
                <Route path="/add-appointment" element={<AuthProtection><AddAnAppointment /></AuthProtection>} />
                <Route path="/gallery" element={<AuthProtection><Gallery /></AuthProtection>} />
                <Route path="/view-package" element={<AuthProtection><ViewPackage /></AuthProtection>} />
                <Route path="/package-detail" element={<AuthProtection><PackageDetails /></AuthProtection>} />
                <Route path="/p/:productId" element={<ProductDetail />} />
                <Route path="/shop" element={<Shop />} />
                {/* A category is a PLACE, not a query string — see
                    `utils/categorySlug.js`. The readable address resolves to the
                    same filter state `?cat=<uuid>` set, and that older form
                    still works, because those links are already in the wild. */}
                <Route path="/shop/c/:categorySlug" element={<Shop />} />
                <Route path="/shop/collections" element={<AllCollectionsPage />} />
                <Route path="/shop/collections/:collectionSlug" element={<CollectionPage />} />
                {/* Brand pages, as collections have (backlog item 26). Keyed by
                    SLUG — `لوريال` is at `/lwryal` — with the id accepted by the
                    API as a compatibility arm for un-backfilled rows. */}
                <Route path="/shop/brands/:brandSlug" element={<BrandPage />} />
                <Route path="/stays" element={<PropertiesStays />} />
                <Route path="/stay/:propertySlug" element={<PropertyStay />} />
                {/* Courses (public, no auth) — plan 7, F-200/201/202 */}
                <Route path="/courses" element={<CourseCatalogue />} />
                <Route path="/courses/:courseSlug" element={<CoursePage />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-placed" element={<OrderPlaced />} />
                <Route path="/appointment-details/:_id" element={<AuthProtection><AppointmentDetails /></AuthProtection>} />
                <Route path="/booking-successful" element={<AuthProtection><CustomerLayout><BookedSuccessfull /></CustomerLayout></AuthProtection>} />
                <Route path="/legal" element={<AuthProtection><LegalPage /></AuthProtection>} />
                <Route path="/terms" element={<AuthProtection><CustomerLayout><TermsOfService /></CustomerLayout></AuthProtection>} />
                <Route path="/privacy" element={<AuthProtection><CustomerLayout><PrivacyPolicy /></CustomerLayout></AuthProtection>} />
                <Route path="/contact-us" element={<AuthProtection><ContactUsPage /></AuthProtection>} />
                <Route path="/see-all-appointments" element={<AuthProtection><CustomerLayout><SeeAllAppointments /></CustomerLayout></AuthProtection>} />

                {/* Customer auth on the branded domain: the Navbar shows a
                    Login entry and "my bookings"/profile require auth, so the
                    login/register/onboard routes MUST resolve here too. (They
                    were previously omitted, so /login fell through to the
                    catch-all and silently re-rendered Home — the "Login does
                    nothing" bug.) Same platform user; AuthProtection's redirects
                    are absolute (/login, /home) so they work slug-less. */}
                <Route path="/login" element={<AuthProtection><LoginOrRegiser /></AuthProtection>} />
                <Route path="/register" element={<AuthProtection><LoginOrRegiser /></AuthProtection>} />
                <Route path="/customer-onboard" element={<AuthProtection><CustomerOnboard /></AuthProtection>} />

                {/* NOT wrapped in AuthProtection: its AUTH_ROUTES treats the
                    literal "/" as a login/register path and would bounce a
                    logged-in customer to /home — wrong here, since "/" IS
                    the tenant storefront (mirrors platform mode, where
                    Home lives at /:username and never collides with "/"). */}
                <Route path="/" element={<Home />} />
                {/* Catch-all: any other path renders the tenant home. */}
                <Route path="*" element={<Home />} />
              </>
            ) : (
              <>
            {/* Public Routes (no Navbar) */}
            {/* Tranzila iframe redirect target — top-level so the customer
                site chrome doesn't render inside the iframe. */}
            <Route path="/payments/iframe-callback" element={<PaymentsIframeCallback />} />
            {/* Public POS pay-by-link landing — no auth required, the
                intentId is the bearer credential. Used when a cashier
                triggers consumeMode='LINK' on a POS card-pay intent. */}
            <Route path="/pay/pos/:intentId" element={<PayPosIntent />} />
            {/* A receipt, keyed by the invoice's own retrieval key — what the receipt
                email and WhatsApp open. Public and slug-less like the pay page. */}
            <Route path="/receipt/:key" element={<Receipt />} />
            <Route path="/" element={<AuthProtection><LoginOrRegiser /></AuthProtection>} />
            <Route path="/login" element={<AuthProtection><LoginOrRegiser /></AuthProtection>} />
            <Route path="/register" element={<AuthProtection><LoginOrRegiser /></AuthProtection>} />
            <Route path="/customer-onboard" element={<AuthProtection><CustomerOnboard /></AuthProtection>} />

            {/* Protected Routes: customer app uses NavbarCustomer (Home, Profile, Logout only) */}
            <Route path="/home" element={<AuthProtection><CustomerHomer /></AuthProtection>} />
            <Route path="/profile" element={<AuthProtection><CustomerLayout><Profile /></CustomerLayout></AuthProtection>} />
            <Route path="/profile/orders"   element={<AuthProtection><CustomerLayout><MyOrders /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/plans"     element={<AuthProtection><CustomerLayout><MyPlans /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/plans/:planId" element={<AuthProtection><CustomerLayout><MyPlans /></CustomerLayout></AuthProtection>} />
                {/* Courses, the student's own — design §13.6, wave 3 (self-paced) */}
                <Route path="/profile/courses" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/courses/:enrollmentId" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                <Route path="/profile/courses/:enrollmentId/lessons/:lessonId" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
                {/* One meeting of a course, opened from the schedule: what it covered,
                    its homework, its place, its files, and the lessons linked to it. */}
                <Route path="/profile/courses/:enrollmentId/meetings/:sessionId" element={<AuthProtection><CustomerLayout><MyCourses /></CustomerLayout></AuthProtection>} />
            <Route path="/orders/:id"       element={<AuthProtection><OrderDetails /></AuthProtection>} />
            {/* Hospitality: authenticated customer stay-details (rich page).
                Public no-login status stays on /:salonSlug/stay/:propertySlug?request=<id>. */}
            <Route path="/stays/:id"        element={<AuthProtection><StayDetails /></AuthProtection>} />
            <Route path="/profile/wishlist" element={<AuthProtection><CustomerLayout><MyWishlist /></CustomerLayout></AuthProtection>} />
            <Route path="/appointments" element={<AuthProtection><CustomerHomer /></AuthProtection>} />
            <Route path="/booking" element={<AuthProtection><CustomerHomer /></AuthProtection>} />

            {/* Add-appointment: uses its own Navbar (no global Navbar in back) */}
            <Route path="/:slug/add-appointment" element={<AuthProtection><AddAnAppointment /></AuthProtection>} />
            <Route path="/add-appointment" element={<AuthProtection><AddAnAppointment /></AuthProtection>} />
            <Route path="/:username/gallery" element={<AuthProtection><Gallery /></AuthProtection>} />
            <Route path="/:username/view-package" element={<AuthProtection><ViewPackage /></AuthProtection>} />
            <Route path="/:username/package-detail" element={<AuthProtection><PackageDetails /></AuthProtection>} />
            {/* Product detail page (Wave C). Public — no auth wrapper so
                the salon-portal "Preview in shop" CTA opens it without
                forcing a login. */}
            <Route path="/:salonSlug/p/:productId" element={<ProductDetail />} />
            <Route path="/:salonSlug/shop" element={<Shop />} />
            <Route path="/:salonSlug/shop/c/:categorySlug" element={<Shop />} />
            <Route path="/:salonSlug/shop/collections" element={<AllCollectionsPage />} />
            <Route path="/:salonSlug/shop/collections/:collectionSlug" element={<CollectionPage />} />
            <Route path="/:salonSlug/shop/brands/:brandSlug" element={<BrandPage />} />
            {/* Hospitality (public, no auth) — must be before /:username */}
            <Route path="/:salonSlug/stays" element={<PropertiesStays />} />
            <Route path="/:salonSlug/stay/:propertySlug" element={<PropertyStay />} />
            {/* Courses (public, no auth) — plan 7, F-200/201/202 */}
            <Route path="/:salonSlug/courses" element={<CourseCatalogue />} />
            <Route path="/:salonSlug/courses/:courseSlug" element={<CoursePage />} />
            <Route path="/:salonSlug/checkout" element={<Checkout />} />
            <Route path="/:salonSlug/order-placed" element={<OrderPlaced />} />
            <Route path="/appointment-details/:_id" element={<AuthProtection><AppointmentDetails /></AuthProtection>} />
            <Route path="/:username/booking-successful" element={<AuthProtection><CustomerLayout><BookedSuccessfull /></CustomerLayout></AuthProtection>} />
            <Route path="/booking-successful" element={<AuthProtection><CustomerLayout><BookedSuccessfull /></CustomerLayout></AuthProtection>} />
            <Route path="/legal" element={<AuthProtection><LegalPage /></AuthProtection>} />
            <Route path="/terms" element={<AuthProtection><CustomerLayout><TermsOfService /></CustomerLayout></AuthProtection>} />
            <Route path="/privacy" element={<AuthProtection><CustomerLayout><PrivacyPolicy /></CustomerLayout></AuthProtection>} />
            <Route path="/contact-us" element={<AuthProtection><ContactUsPage /></AuthProtection>} />
            <Route path="/see-all-appointments" element={<AuthProtection><CustomerLayout><SeeAllAppointments /></CustomerLayout></AuthProtection>} />

            {/* SEO discovery routes — public, no auth required, must be before /:username */}
            <Route path="/category/:categorySlug" element={<CategoryPage />} />
            <Route path="/city/:citySlug/:categorySlug" element={<CityPage />} />
            <Route path="/city/:citySlug" element={<CityPage />} />
            <Route path="/stores" element={<AllBusinessesPage />} />
            <Route path="/explore" element={<AllBusinessesPage />} />

            <Route path="/:username" element={<AuthProtection><Home /></AuthProtection>} />

            {/* Catch all route - redirect to login (no Navbar) */}
            <Route path="*" element={<AuthProtection><LoginOrRegiser /></AuthProtection>} />
              </>
            )}
              </Routes>
              </Suspense>
              </ShopLanguageGate>
              <StickyCartBar />
              <ShopFloatingDock />
              {/* The customer agent, on every page of a shop that switched it on (customer agent, 10-21). */}
              <ShopAgentChat />
              <CartAbandonmentTracker />
              </CurrencyProvider>
              </CartProvider>
            </ThemeProvider>
          </Router>
        </Provider>
      </AuthProvider>
    </div>
  );
}

export default App;
